/**
 * Query keys, in one place, because the shape of a key is a tenancy control.
 *
 * HARD RULE: every key for workspace-scoped server state begins
 * `['ws', workspaceId, …]`.
 *
 * Two reasons, both of which have bitten real apps:
 *
 *   1. Cache bleed. Rundown is multi-tenant and a user can belong to more than
 *      one workspace. A key like ['requests'] would serve workspace A's rows to
 *      workspace B on a switch, and the user would have no way to know.
 *   2. Invalidation. A realtime event carries a workspaceId and nothing else
 *      reliable, so `invalidateQueries({ queryKey: ['ws', id] })` can blow away
 *      exactly one tenant's cache and nothing else. That only works if the id is
 *      the first discriminator — TanStack matches keys by prefix.
 *
 * The profile key is the deliberate exception: identity is what *tells* us which
 * workspaces exist, so it cannot itself be scoped to one.
 *
 * ── "In one place" is now true ──────────────────────────────────────────────
 * It was not. There were three registries: this one, `features/activity/
 * queryKeys.ts` and `features/notifications/queryKeys.ts`. Both of the others
 * opened with a note saying they existed only because this file belonged to
 * somebody else that week and should be folded back in — and the activity one
 * described its own keys as "value-identical" to the ones already here. A file
 * whose header claims a single source of truth, with two shadow copies beside
 * it, is worse than no claim.
 */

export const queryKeys = {
  /** The signed-in user and their memberships. Not workspace-scoped, by definition. */
  profile: () => ['auth', 'profile'] as const,

  /** Prefix for everything belonging to one workspace. Invalidate to reset a tenant. */
  workspace: (workspaceId: string) => ['ws', workspaceId] as const,

  requests: (workspaceId: string) => ['ws', workspaceId, 'requests'] as const,
  /**
   * Home's Posts tab: the same rows, newest first. A CHILD of `requests` on
   * purpose, unlike the review queue below — it holds the same rows under a
   * different order, so every existing request invalidation should refresh it
   * too, and prefix matching does that for free.
   */
  /**
   * The Review inbox. A sibling of `requests` rather than a child of it: making
   * it ['…','requests','review'] would put it under the prefix every request
   * mutation invalidates, so it would refetch on changes that cannot affect it.
   */
  reviewQueue: (workspaceId: string) => ['ws', workspaceId, 'review-queue'] as const,
  request: (workspaceId: string, requestId: string) => ['ws', workspaceId, 'requests', requestId] as const,
  requestMessages: (workspaceId: string, requestId: string) =>
    ['ws', workspaceId, 'requests', requestId, 'messages'] as const,
  requestAttachments: (workspaceId: string, requestId: string) =>
    ['ws', workspaceId, 'requests', requestId, 'attachments'] as const,

  sprints: (workspaceId: string) => ['ws', workspaceId, 'sprints'] as const,
  backlog: (workspaceId: string) => ['ws', workspaceId, 'backlog'] as const,
  /** The projection is a derived read of the backlog, keyed by the two view switches. */
  projection: (workspaceId: string, capacityMode: string, fill: string) =>
    ['ws', workspaceId, 'backlog', 'projection', capacityMode, fill] as const,

  search: (workspaceId: string, query: string, limit: number) =>
    ['ws', workspaceId, 'search', query, limit] as const,
  activity: (workspaceId: string) => ['ws', workspaceId, 'activity'] as const,
  requestActivity: (workspaceId: string, requestId: string) =>
    ['ws', workspaceId, 'requests', requestId, 'activity'] as const,

  settingsWorkspace: (workspaceId: string) => ['ws', workspaceId, 'settings', 'workspace'] as const,
  settingsSecrets: (workspaceId: string) => ['ws', workspaceId, 'settings', 'secrets'] as const,
  settingsPeople: (workspaceId: string) => ['ws', workspaceId, 'settings', 'people'] as const,
  settingsProjects: (workspaceId: string) => ['ws', workspaceId, 'settings', 'projects'] as const,
  board: (workspaceId: string) => ['ws', workspaceId, 'board'] as const,

  /**
   * Notifications. `all` is the shared prefix the two below sit under, so a
   * realtime-driven `invalidateQueries({ queryKey: queryKeys.notifications(ws) })`
   * catches the list, the count and anything added later without naming them.
   */
  /** Home's thread sidebar, and every thread opened from it. */
  threads: (workspaceId: string) => ['ws', workspaceId, 'threads'] as const,
  threadList: (workspaceId: string, filter: string, search = '') =>
    ['ws', workspaceId, 'threads', 'list', filter, search] as const,
  threadCounts: (workspaceId: string) => ['ws', workspaceId, 'threads', 'counts'] as const,
  thread: (workspaceId: string, threadId: string) => ['ws', workspaceId, 'threads', 'one', threadId] as const,

  notifications: (workspaceId: string) => ['ws', workspaceId, 'notifications'] as const,
  notificationsList: (workspaceId: string, unreadOnly: boolean) =>
    ['ws', workspaceId, 'notifications', 'list', unreadOnly] as const,
  notificationsCount: (workspaceId: string) => ['ws', workspaceId, 'notifications', 'count'] as const,
} as const;
